/**
 * RXD-02..07 — uma frase ditada vira os campos da Nova receita: paciente (resolvido localmente,
 * nunca adivinhado), itens via `generate-document-draft`, remédio casado com o catálogo de
 * princípios ativos, quantidade calculada e subtipo pelo tipo de receita Anvisa.
 *
 * Nada aqui salva: o resultado só preenche o formulário para o médico conferir (RXD-10).
 */
import type { Patient } from '@/entities/patient/types'
import { requestParsedDocumentDraft } from '@/shared/api/generateDocumentDraftApi'
import type { ParsedPrescriptionItemDraft } from '@/shared/api/parseDocumentDraftResponse'
import type { PrescriptionSignatureKind } from '@/shared/lib/digitalSignatureDocumentType'
import { normalizeIdentity } from '@/shared/lib/normalize'
import { mapTipoReceitaToSignatureKind } from '@/shared/prescription/mapTipoReceitaToSignatureKind'
import {
  dosesPerDay,
  durationInDays,
  formatInclusionLine,
  type PrescriptionFrequencyShortcut,
  type PrescriptionPeriodUnit,
} from '@/shared/prescription/suggestPrescriptionCapsules'
import type { ActiveIngredientRecord } from '@/shared/repositories/activeIngredients/activeIngredientsRepository'

export interface PatientNameMatch {
  status: 'unique' | 'ambiguous' | 'none'
  /** Nome como foi ouvido (vazio quando a frase não traz paciente). */
  query: string
  candidates: Patient[]
}

export interface DictationQuantity {
  quantity: number
  /** «2 por dia × 60 dias». */
  explanation: string
}

export interface DictatedItem {
  medicationName: string
  /** Nome ouvido quando o catálogo corrigiu a grafia. */
  heardName: string | null
  catalog: Pick<ActiveIngredientRecord, 'id' | 'name' | 'tipoReceita'> | null
  /** Sem match no catálogo: fica como texto marcado para revisar (RXD-05). */
  needsReview: boolean
  frequencyShortcut: PrescriptionFrequencyShortcut | null
  scheduleText: string
  periodAmount: number | null
  periodUnit: PrescriptionPeriodUnit | null
  quantity: DictationQuantity | null
  instructions: string
}

export interface DictationResult {
  patient: PatientNameMatch
  items: DictatedItem[]
  /** Subtipo sugerido pelo catálogo (o mais restritivo entre os itens). */
  prescriptionKind: PrescriptionSignatureKind | null
  warnings: string[]
}

export type CatalogLookup = (heardName: string) => Promise<Array<Pick<ActiveIngredientRecord, 'id' | 'name' | 'tipoReceita'>>>

const CONTINUOUS_QUANTITY = 90
const PATIENT_PHRASE = /\b(?:[Pp]ara|[Pp]ra|[Pp]aciente)\s+(?:[ao]\s+)?(?:paciente\s+)?([A-ZÀ-Ý][\p{L}'-]+(?:\s+(?:d[aeo]s?\s+)?[A-ZÀ-Ý][\p{L}'-]+)*)\s*[,.:;-]?\s*/u
const KIND_RANK: Record<PrescriptionSignatureKind, number> = {
  prescription_simple: 0,
  prescription_antimicrobial: 1,
  prescription_controlled: 2,
}

const LOWERCASE_PATIENT_PHRASE = /\b(?:para|pra|paciente)\s+(?:[ao]\s+)?((?:[\p{L}'-]+\s*){1,4})/iu

/**
 * Separa «para Ana Souza, …» do resto da frase. O reconhecimento de voz costuma vir em
 * minúsculas: aí vale o maior trecho (até 4 palavras) que casa com algum paciente.
 */
export function extractPatientName(text: string, patients: ReadonlyArray<Patient> = []): { name: string; rest: string } {
  const cut = (index: number, length: number, name: string) => ({
    name,
    rest: `${text.slice(0, index)} ${text.slice(index + length)}`.replace(/\s+/g, ' ').replace(/^[\s,.;:-]+/, '').trim(),
  })
  const match = PATIENT_PHRASE.exec(text)
  if (match) return cut(match.index, match[0].length, match[1]!.trim())
  const loose = LOWERCASE_PATIENT_PHRASE.exec(text)
  if (loose) {
    const words = loose[1]!.trim().split(/\s+/)
    for (let size = words.length; size >= 1; size -= 1) {
      const name = words.slice(0, size).join(' ')
      if (resolvePatientByName(name, patients).status === 'none') continue
      const prefix = loose[0].slice(0, loose[0].length - loose[1]!.length)
      const consumed = `${prefix}${name}`
      const after = text.slice(loose.index + consumed.length).match(/^\s*[,.:;-]?\s*/)?.[0] ?? ''
      return cut(loose.index, consumed.length + after.length, name)
    }
  }
  return { name: '', rest: text.trim() }
}

/**
 * Nome completo igual → único. Senão, pacientes cujo nome começa pelas mesmas palavras (cada
 * palavra ouvida casa com o início de uma palavra do nome, na ordem).
 */
export function resolvePatientByName(name: string, patients: ReadonlyArray<Patient>): PatientNameMatch {
  const query = normalizeIdentity(name)
  if (!query) return { status: 'none', query: '', candidates: [] }
  const exact = patients.filter((patient) => normalizeIdentity(patient.name) === query)
  if (exact.length === 1) return { status: 'unique', query: name.trim(), candidates: exact }
  const tokens = query.split(' ')
  const candidates = exact.length
    ? exact
    : patients.filter((patient) => {
        const words = normalizeIdentity(patient.name).split(' ')
        let at = 0
        return tokens.every((token) => {
          while (at < words.length && !words[at]!.startsWith(token)) at += 1
          return at++ < words.length
        })
      })
  const status = candidates.length === 0 ? 'none' : candidates.length === 1 ? 'unique' : 'ambiguous'
  return { status, query: name.trim(), candidates }
}

/** Doses por dia × dias (RXD-03); contínuo sugere 90. `null` quando falta frequência ou prazo. */
export function computeQuantity(input: {
  frequencyShortcut: PrescriptionFrequencyShortcut | null
  scheduleText?: string
  periodAmount: number | null
  periodUnit: PrescriptionPeriodUnit | null
}): DictationQuantity | null {
  if (input.periodUnit === 'continuo') return { quantity: CONTINUOUS_QUANTITY, explanation: 'uso contínuo' }
  const doses = dosesPerDay(input.scheduleText ?? '', input.frequencyShortcut)
  const days = durationInDays(input.periodAmount, input.periodUnit)
  if (!doses || !days) return null
  return { quantity: doses * days, explanation: `${doses} por dia × ${days} dias` }
}

/** O catálogo aceita grafias próximas («glicazida» → «gliclazida») pelo início da palavra. */
function pickCatalogMatch(heard: string, results: Awaited<ReturnType<CatalogLookup>>) {
  const normalized = normalizeIdentity(heard)
  const exact = results.find((record) => normalizeIdentity(record.name) === normalized)
  if (exact) return exact
  const prefix = normalized.slice(0, 4)
  return results.find((record) => normalizeIdentity(record.name).startsWith(prefix)) ?? null
}

async function toDictatedItem(raw: ParsedPrescriptionItemDraft, lookup: CatalogLookup): Promise<DictatedItem> {
  const heard = raw.medicationName.trim()
  const results = await lookup(heard).catch(() => [])
  const catalog = pickCatalogMatch(heard, results)
  const corrected = catalog && normalizeIdentity(catalog.name) !== normalizeIdentity(heard)
  return {
    medicationName: catalog?.name ?? heard,
    heardName: corrected ? heard : null,
    catalog,
    needsReview: !catalog,
    frequencyShortcut: raw.frequencyShortcut,
    scheduleText: raw.scheduleText,
    periodAmount: raw.periodAmount,
    periodUnit: raw.periodUnit,
    quantity: computeQuantity(raw),
    instructions: raw.instructions,
  }
}

/** Subtipo mais restritivo entre os remédios do catálogo (RXD-07). */
export function prescriptionKindForItems(items: ReadonlyArray<Pick<DictatedItem, 'catalog'>>): PrescriptionSignatureKind | null {
  let best: PrescriptionSignatureKind | null = null
  for (const item of items) {
    const kind = item.catalog ? mapTipoReceitaToSignatureKind(item.catalog.tipoReceita) : null
    if (kind && (!best || KIND_RANK[kind] > KIND_RANK[best])) best = kind
  }
  return best
}

export async function parseDictation(
  text: string,
  deps: {
    patients: ReadonlyArray<Patient>
    catalogLookup: CatalogLookup
    requestItems?: (phrase: string) => Promise<ParsedPrescriptionItemDraft[]>
  },
): Promise<DictationResult> {
  const { name, rest } = extractPatientName(text, deps.patients)
  const requestItems =
    deps.requestItems ??
    (async (phrase: string) => {
      const parsed = await requestParsedDocumentDraft('prescription', {
        surface: 'document',
        documentType: 'prescription',
        documentTypeLabel: 'Receita',
        title: '',
        contentDraft: phrase,
      })
      return parsed.kind === 'prescription' ? parsed.items : []
    })
  const rawItems = rest ? await requestItems(rest) : []
  const items = await Promise.all(rawItems.map((raw) => toDictatedItem(raw, deps.catalogLookup)))
  const patient = resolvePatientByName(name, deps.patients)
  const warnings: string[] = []
  if (patient.status === 'ambiguous') warnings.push(`Qual ${patient.query}?`)
  if (patient.status === 'none' && patient.query) warnings.push(`Paciente «${patient.query}» não encontrado.`)
  for (const item of items) {
    if (item.heardName) warnings.push(`ouvido «${item.heardName}»`)
    if (item.needsReview) warnings.push(`Revise «${item.medicationName}»: não está no catálogo.`)
  }
  return { patient, items, prescriptionKind: prescriptionKindForItems(items), warnings }
}

/** Item no formato do compositor da receita («Editar tudo», RXD-08). */
export function dictatedItemToComposer(item: DictatedItem) {
  const capsules = item.quantity?.quantity ?? 0
  return {
    medicationName: item.medicationName,
    frequencyShortcut: item.frequencyShortcut,
    scheduleText: item.scheduleText,
    periodAmount: item.periodAmount,
    periodUnit: item.periodUnit,
    capsules,
    inclusionLine: formatInclusionLine(item.medicationName, capsules),
    instructions: item.instructions,
  }
}
